import { useEffect, useRef, useState } from "react";
import { clearPost, fetchPosts, reportPost } from "app/actions/posts";
import { fetchScheduledPosts, clearSchedulePost } from "app/actions/schedule";
import { useActions } from "app/utils/hooks";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { deletePost, editPost } from "app/actions/postingForm";
import { allowMultipleSelect } from "app/actions/destinations";
import moment from "moment";
import { useParams } from "react-router-dom";
import PostModel from "app/models/PostModel";
import { FilterModel } from "app/models/FilterModel";
import { usePermissionChecker } from "app/utils/hooks/usePermission";
import useInfiniteScroll from "react-infinite-scroll-hook";

export const SpacesTimelineModel = () => {
  const urlParams = useParams<{ id: string }>();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const dispatch = useDispatch();
  const [scheduledPostLocation, setScheduledPostLocation] = useState("");
  const postsActions = useActions({
    fetchPosts,
    fetchScheduledPosts,
    editPost,
    clearPost,
    reportPost,
    deletePost,
  });

  const { selectedSpace } = useSelector(
    (state: RootState) => state.space,
    shallowEqual,
  );

  const { scheduledPostsSuccess: scheduledPostsData } = useSelector(
    (state: RootState) => state.schedulePost,
    shallowEqual,
  );
  const abortControllerRef = useRef<AbortController | null>(null);

  const { can } = usePermissionChecker(self?.id, selectedSpace);

  const [newScehduleModalOpen, setNewScheduleModalOpen] =
    useState<boolean>(false);

  const [scheduledPostsParams] = useState({
    teamId: urlParams.id,
    "options[include]": ["posts"],
    orderBy: "scheduled_at",
    order: "asc",
    timezoneOffset: moment().utcOffset().toString(),
  });

  const postsParams = {
    "options[include]": ["replies"],
    limit: 20,
    skip: 0,
    reply_limit: 3,
    throttle: 100000,
    space_id: selectedSpace?.id,
  };

  const [scheduledAt, setScheduledAt] = useState(null);
  const [reportModal, setReportModal] = useState({ isOpen: false, post: null });
  const [data, setData] = useState<PostModel[]>([]);

  const [query, setQuery] = useState<{
    limit?: number;
    skip?: number;
    reply_limit?: number;
  }>({ ...postsParams, skip: 0 });

  const handleNewSchedulePost = (): void => {
    setNewScheduleModalOpen((prev: boolean): boolean => !prev);
  };

  const onReport = (post: PostModel, message?: string) => {
    postsActions
      .reportPost(post.id, message)
      .then(() => {
        setData((prevData) =>
          prevData.filter(
            (filteredPost: PostModel) => filteredPost.id !== post.id,
          ),
        );
      })
      .catch((error) => {
        console.error("Error deleting post:", error);
      });
  };

  const onPostReportClick = (post: PostModel) => {
    setReportModal({ isOpen: true, post });
  };

  const reportModalClose = () => {
    setReportModal({ isOpen: false, post: null });
  };

  const onReportHandler = (reason: string) => {
    onReport(reportModal?.post, reason);
    reportModalClose();
  };

  const fetchPage = async (queryVal: {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      const res = await postsActions.fetchPosts(
        queryVal,
        abortControllerRef.current.signal,
      );
      if (!abortControllerRef?.current?.signal?.aborted) {
        if (res) {
          setData((prevData: any) => [...prevData, ...res]);
          setQuery(queryVal);
        } else {
          console.error("API response is null or undefined");
        }
      }
    } catch (error) {
      if (!abortControllerRef.current.signal.aborted) {
        console.error("Error fetching posts:", error);
      }
    }
  };

  const initialLoadPosts = (queryVal: {}) => {
    setData([]);
    postsActions.clearPost();
    fetchPage({ ...queryVal, skip: 0 });
  };

  const loadMore = () => {
    if (!pending) {
      const newQuery = { ...query, skip: query.skip + postsParams.limit };
      fetchPage(newQuery);
    }
  };

  const { pending, hasMore } = useSelector(
    (state: RootState) => state.posts,
    shallowEqual,
  );

  const [sentryRef] = useInfiniteScroll({
    loading: pending,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  useEffect(() => {
    initialLoadPosts({ ...postsParams, skip: 0 });
    dispatch(allowMultipleSelect(true));
    return () => {
      dispatch(clearSchedulePost());
      postsActions.clearPost();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setData([]);
      setQuery(postsParams);
    };
  }, [dispatch, postsActions]);

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      dispatch(allowMultipleSelect(true));
      postsActions.fetchScheduledPosts(scheduledPostsParams);
    }

    return () => {
      dispatch(clearSchedulePost());
      postsActions.clearPost();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setQuery(postsParams);
      mounted = false;
    };
  }, [postsActions, dispatch, urlParams.id]);

  const onPostDelete = async (post: PostModel) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this post?",
    );
    if (confirmation) {
      await postsActions.deletePost(post.id);
      setData((prevData) =>
        prevData.filter(
          (filteredPost: PostModel) => filteredPost.id !== post.id,
        ),
      );
    }
  };

  const onPostUpdate = async (post: PostModel) => {
    if (post) {
      await postsActions.editPost(post);
    }
  };

  const onPostPin = () => {
    initialLoadPosts({ ...postsParams, skip: 0 });
  };

  function filterHandler(filters: FilterModel) {
    const filteredQuery = { ...postsParams, skip: 0, ...filters };
    postsActions.clearPost();
    initialLoadPosts(filteredQuery);
  }

  function onClearFilters() {
    postsActions.clearPost();
    initialLoadPosts({ ...postsParams, skip: 0 });
  }

  const onPost = async (post: PostModel) => {
    if (!post) {
      return;
    }

    if (!post?.scheduledAt) {
      if (!data?.[0]?.pinnedByName) {
        setData((prevData) => [
          { ...post, userAvatar: self.avatar },
          ...(prevData || []),
        ]);
      } else if (data?.[0]?.pinnedByName) {
        // if the first post is pinned, insert the new post at the second index
        setData((prevData) => {
          const newData = prevData?.length > 0 ? [...prevData] : [];
          newData.splice(1, 0, {
            ...post,
            userAvatar: self.avatar,
          });
          return newData;
        });
      }
    } else {
      setScheduledPostLocation(`/spaces/${post.spaceId}/schedule`);
      postsActions.fetchScheduledPosts(scheduledPostsParams);
      setScheduledAt(post.scheduledAt);
      handleNewSchedulePost();
    }
  };

  const canSeePostviews = can("access_views");

  const canSeeWhoLiked = can("access_likes");

  return {
    selectedSpace,
    spaceId: urlParams.id,
    data,
    scheduledPostsData,
    onPost,
    pending,
    reportModal,
    reportModalClose,
    onReportHandler,
    onPostReportClick,
    onPostDelete,
    onPostUpdate,
    onPostPin,
    sentryRef,
    filterHandler,
    onClearFilters,
    newScehduleModalOpen,
    handleNewSchedulePost,
    scheduledAt,
    scheduledPostLocation,
    canSeePostviews,
    canSeeWhoLiked,
  };
};
